import { CSSProperties, ComponentPropsWithoutRef, ElementType, Fragment, ReactNode } from 'react';

import {
  useFloating,
  Placement,
  flip,
  MiddlewareData,
  detectOverflow,
  Middleware,
  autoUpdate,
  size,
  offset,
} from '@floating-ui/react-dom';
import { Popover as HeadlessPopover, Portal } from '@headlessui/react';
import { HTMLMotionProps, motion } from 'framer-motion';
import styled, { CSSObject } from 'styled-components';

import { HEADLESS_PORTAL_ID } from '@components/menu';
import { dropdownMotionConfig } from '@styles/animation';
import { theme } from '@styles/theme';
import { getVisibleHeight } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { Button } from '../button';

type ChildrenFn = (props: { isOpen: boolean; close: () => void }) => ReactNode;

export type PopoverButtonProps<T extends ElementType = any> = {
  trigger?: T;
  triggerProps: ComponentPropsWithoutRef<T>;
  children: ReactNode | ChildrenFn;
  contentStyle?: CSSObject;
  popoverStyle?: CSSObject;
  placement?: Placement;
  motionProps?: HTMLMotionProps<any>;
  offset?: { mainAxis?: number; crossAxis?: number };
  panelOverflow?: CSSProperties['overflow'];
};

export function PopoverButton<T extends ElementType>(props: PopoverButtonProps<T>) {
  const {
    trigger = Button,
    triggerProps,
    children,
    contentStyle = {},
    popoverStyle = {},
    placement = 'bottom-start',
    motionProps = dropdownMotionConfig,
    offset: floatOffset,
    panelOverflow,
  } = props;

  const { x, y, refs, strategy } = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      floatOffset && offset(floatOffset),
      flip(),
      moveUpMiddleware,
      size({
        apply: handlePopoverSizeChange,
      }),
    ],
  });

  return (
    <HeadlessPopover style={popoverStyle}>
      {({ close, open }) => (
        <>
          <HeadlessPopover.Button
            as={trigger}
            ref={refs.setReference}
            {...triggerProps}
            isOpen={open}
          />

          <Portal>
            <div id={HEADLESS_PORTAL_ID}>
              <HeadlessPopover.Panel
                className="popover-panel"
                ref={refs.setFloating}
                as={StyledPanel}
                style={{
                  ...contentStyle,
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                  overflow: panelOverflow,
                }}
                {...motionProps}
              >
                <HeadlessPopover.Overlay />
                {typeof children === 'function' ? children({ isOpen: open, close }) : children}
              </HeadlessPopover.Panel>
            </div>
          </Portal>
        </>
      )}
    </HeadlessPopover>
  );
}

export const handlePopoverSizeChange = ({
  rects,
  elements,
}: MiddlewareData & { availableHeight: number }) => {
  const height = getVisibleHeight();
  const tooTall = height < rects.floating.height + 40;

  if (tooTall) {
    elements.floating.style.maxHeight = `${height - 20}px`;
  } else if (elements.floating.style.maxHeight) {
    elements.floating.style.maxHeight = '';
  }
};

export const moveUpMiddleware: Middleware = {
  name: 'move-up-middleware',
  options: {},
  async fn(state) {
    // Move the popover up so it stays in view
    const overflow = await detectOverflow(state);
    const adjustY = overflow.bottom > 0 || overflow.top > 0 ? overflow.top + overflow.bottom : 0;
    if (!adjustY) {
      return {};
    }

    return {
      y: Math.abs(overflow.top + overflow.bottom) - 10,
    };
  },
};

const StyledPanel = styled(motion.div)({
  position: 'absolute',
  zIndex: 9999,
  width: '100%',
  maxWidth: '16rem',
  overflow: 'auto',
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.backgroundLightDark,
  color: theme.color.textPrimary,
  boxShadow: theme.shadow.high,
  padding: '1rem',
});
